// Parts desk: one screen for the parts person — mechanics waiting, lines without a price, wrong-part flags, low stock and orders coming in.
import { useState } from 'react';
import { api, useApi, navigate, ago, photoSrc, dollars, setQuery, currentMe, type Row } from '../lib';
import { Button, Loading, ErrorBox, useToast, confirmAction } from '../ui';
import { OrderEditor, OrderModal } from './Purchasing';

type Desk = { waiting: Row[]; price: Row[]; flagged: Row[]; orders: Row[] };

function Box({ icon, title, count, tone, children, wide }: { icon: string; title: string; count: number; tone: string; children: React.ReactNode; wide?: boolean }) {
  return (
    <section className={'desk-box' + (wide ? ' wide' : '')}>
      <h2><span>{icon} {title}</span><span className={'desk-cnt ' + (count ? tone : 'zero')}>{count}</span></h2>
      {count ? children : <p className="muted desk-none">Nothing here — all good.</p>}
    </section>
  );
}

const woLabel = (r: Row) => `${r.unit ? '#' + r.unit + ' · ' : ''}${r.number}`;

export function PartsDesk() {
  const { data, error, reload } = useApi<Desk>('/api/parts-desk');
  const low = useApi<Row[]>('/api/purchase-orders/suggestions');
  const ret = useApi<Row[]>('/api/cores');
  const alertDays = Number(currentMe()?.shop.return_alert_days || 14);
  const [editing, setEditing] = useState<Row | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const toast = useToast();
  const refresh = () => { reload(); low.reload(); ret.reload(); };
  const act = async (fn: () => Promise<unknown>, msg: string) => { try { await fn(); toast(msg); refresh(); } catch (e) { toast((e as Error).message, 'err'); } };
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!data || !low.data) return <Loading />;
  const lowLine = (p: Row) => ({ part_id: p.id, sku: p.sku, description: p.name, qty: p.suggest, unit_cost: p.cost, vendor: p.supplier || '', stock: p.stock, min: p.min_stock });
  return (
    <>
      <p className="muted desk-intro">Everything that needs the parts person today — in one place.</p>
      <div className="desk">
        <Box icon="⏸" title="Mechanics waiting for parts" count={data.waiting.length} tone="red">
          {data.waiting.map(j => {
            const arrived = String(j.waiting_note || '').startsWith('✓ Parts arrived');
            return (
              <div key={j.id} className="desk-row">
                <div><b className="link" onClick={() => navigate(`/work-orders/${j.wo_id}`)}>{woLabel(j)} · {j.title}</b>
                  <small>{j.mechanic || 'No mechanic'}{j.waiting_note ? `: “${String(j.waiting_note).replace(/^✓ Parts arrived — /, '')}”` : ''} · {ago(j.waiting_at)}</small></div>
                <div className="desk-acts">
                  {j.po_id ? <Button onClick={() => setOpen(j.po_id)}>On {j.po_number}</Button>
                    : <Button kind="primary" onClick={() => setEditing({ job_id: j.id, note: `${woLabel(j)} · ${j.title}`, lines: j.waiting_note ? [{ sku: '', description: String(j.waiting_note).slice(0, 200), qty: 1, unit_cost: 0 }] : [] })}>Order</Button>}
                  {arrived ? <span className="ok-text">✓ Told {j.mechanic || 'mechanic'}</span>
                    : <Button className="go" onClick={() => act(() => api(`/api/jobs/${j.id}/parts-arrived`, { body: {} }), `${j.mechanic || 'Mechanic'} got a notification`)}>Parts arrived ✓</Button>}
                </div>
              </div>
            );
          })}
        </Box>

        <Box icon="💲" title="Price needed — not in inventory" count={data.price.length} tone="amber">
          {data.price.map(l => (
            <div key={l.id} className="desk-row">
              <div><b>{l.description}{l.qty > 1 ? ` × ${l.qty}` : ''}</b>
                <small>{woLabel(l)}{l.part_no ? ` · ${l.part_no}` : ''} · added by {l.by_name || '—'}{l.photo_id ? <> · <a href={photoSrc(l.photo_id)} target="_blank" rel="noreferrer">photo 📷</a></> : ''}</small></div>
              <div className="desk-acts">
                <Button onClick={() => navigate(`/work-orders/${l.wo_id}`)}>It's in inventory</Button>
                <Button kind="primary" onClick={() => navigate(`/work-orders/${l.wo_id}`)}>Set price</Button>
              </div>
            </div>
          ))}
        </Box>

        <Box icon="⚑" title="Wrong / defective — mechanic flagged" count={data.flagged.length} tone="red">
          {data.flagged.map(l => (
            <div key={l.id} className="desk-row">
              <div><b className="link" onClick={() => navigate(`/work-orders/${l.wo_id}`)}>{l.description}{l.qty > 1 ? ` × ${l.qty}` : ''}</b>
                <small>{woLabel(l)} · {l.flag_by_name || 'Mechanic'}: “{l.flag_note}”</small></div>
              <div className="desk-acts">
                <Button kind="primary" onClick={() => act(() => api(`/api/work-orders/${l.wo_id}/parts/${l.id}/return`, { body: { kind: /^defect/i.test(l.flag_note) ? 'defective' : /^not used/i.test(l.flag_note) ? 'unused' : 'wrong' } }), 'On the Returns list — to go back to the vendor')}>↩ Return to vendor</Button>
                <Button onClick={() => { if (confirmAction(`Remove ${l.description} from ${l.number}? It goes back on the shelf.`)) act(() => api(`/api/work-orders/${l.wo_id}/parts/${l.id}`, { method: 'DELETE' }), 'Part removed — back on the shelf'); }}>Back on shelf</Button>
                <Button onClick={() => act(() => api(`/api/work-orders/${l.wo_id}/parts/${l.id}/flag`, { method: 'PUT', body: { clear: true } }), 'Kept — flag cleared')}>Keep</Button>
              </div>
            </div>
          ))}
        </Box>

        <Box icon="📉" title="Running low" count={low.data.length} tone="blue">
          {low.data.slice(0, 12).map(p => (
            <div key={p.id} className="desk-row">
              <div><b>{p.name}</b><small>{p.stock} left · min {p.min_stock}{p.supplier ? ` · ${p.supplier}` : ''}</small></div>
              <div className="desk-acts"><Button onClick={() => setEditing({ vendor: p.supplier || '', lines: [lowLine(p)] })}>+ order {p.suggest}</Button></div>
            </div>
          ))}
          {low.data.length > 12 && <p className="muted">…and {low.data.length - 12} more.</p>}
          <div className="desk-foot"><Button kind="primary" onClick={() => setEditing({ lines: low.data!.map(lowLine) })}>Order all low stock</Button></div>
        </Box>

        {(() => {
          const open = (ret.data || []).filter(r => !r.returned_at && !r.credited_at);
          const groups = Object.entries(open.reduce((m, r) => { (m[r.vendor] = m[r.vendor] || []).push(r); return m; }, {} as Record<string, Row[]>));
          const days = (r: Row) => Math.floor((Date.now() - Date.parse(r.created_at)) / 864e5);
          return <Box icon="↩" title="To return to vendors" count={open.reduce((a, r) => a + r.qty, 0)} tone="amber" wide>
            {groups.map(([v, list]) => { const old = Math.max(...list.map(days)); return (
              <div key={v} className="desk-row">
                <div><b>{v}</b><small>{list.map(r => `${r.qty > 1 ? r.qty + '× ' : ''}${r.description || r.part_no}`).join(' · ')} · {dollars(list.reduce((a, r) => a + r.qty * r.amount, 0))}</small></div>
                <div className="desk-acts"><span className={old >= alertDays ? 'danger' : 'muted'}>{old ? `${old} day${old === 1 ? '' : 's'}` : 'today'}</span><Button onClick={() => setQuery({ tab: 'cores' })}>Open</Button></div>
              </div>); })}
          </Box>;
        })()}
        <Box icon="📦" title="Coming in — open orders" count={data.orders.length} tone="blue" wide>
          {data.orders.map(o => (
            <div key={o.id} className="desk-row">
              <div><b className="link" onClick={() => setOpen(o.id)}>{o.number} · {o.vendor}</b>
                <small>{o.line_count} part{o.line_count === 1 ? '' : 's'} · {o.status === 'sent' ? 'ordered ' + ago(o.sent_at || o.created_at) : 'not sent yet'}</small></div>
              <div className="desk-acts"><Button className="go" onClick={() => setOpen(o.id)}>{o.status === 'sent' ? 'Receive' : 'Open'}</Button></div>
            </div>
          ))}
        </Box>
      </div>
      {editing && <OrderEditor initial={editing} onClose={() => setEditing(null)} onDone={() => { setEditing(null); refresh(); }} />}
      {open && <OrderModal id={open} onClose={() => { setOpen(null); refresh(); }} onEdit={o => { setOpen(null); setEditing(o); }} />}
    </>
  );
}
